import React, { useEffect, useMemo } from 'react'
import { observer, inject } from 'mobx-react'
import { Button, Card, Empty, message, Pagination, Tooltip } from 'antd'
import moment from 'moment'

// Components
import TableComponent from '../../../components/Common/TableComponent'
import UnreadText from '../../../components/UnreadText/UnreadText'
import { EmptyText, TextWrap } from '../../../components/Common/CellText'
import { StatusTag } from '../../../components/Common/StatusTag'

// Constants
import { TYPE_STATUS, DATE_FORMAT_DEFAULT } from '../../../constants'

// Styled
import {
  TableActionWrapper,
  TableFooterActionWrapper,
} from './InternalDocumentDeletedPageStyled'
import { UndoOutlined } from '@ant-design/icons'

const IncomingPanel = inject(
  'internalDocumentStore',
  'loadingAnimationStore'
)(
  observer(({ internalDocumentStore, loadingAnimationStore }) => {
    const {
      getInternalDocumentIncomingDeletedList,
      internalDocumentIncomingDeletedList,
      internalDocumentIncomingListTotalCount,
      queryParamsDeleted,
      setQueryParamsDeleted,
      restoreInternalDocumentById,
    } = internalDocumentStore

    const pageSize = queryParamsDeleted?.size || 10
    const currentPage = queryParamsDeleted?.page || 0

    useEffect(() => {
      const fetchData = async () => {
        try {
          loadingAnimationStore.setTableLoading(true)
          await getInternalDocumentIncomingDeletedList()
        } catch (error) {
          message.error('Không thể tải danh sách văn bản đã xoá')
        } finally {
          loadingAnimationStore.setTableLoading(false)
        }
      }

      fetchData()
    }, [queryParamsDeleted.page])

    const handleRestoreDocument = async record => {
      try {
        loadingAnimationStore.setTableLoading(true)
        await restoreInternalDocumentById(record.code)
        message.success('Khôi phục văn bản thành công')
        await getInternalDocumentIncomingDeletedList()
      } catch (error) {
        message.error('Khôi phục văn bản thất bại')
      } finally {
        loadingAnimationStore.setTableLoading(false)
      }
    }

    const tableColumns = useMemo(() => {
      const WIDTH = {
        incoming: 60,
        number: 120,
        incomingDate: 100,
        issuedDate: 100,
        status: 120,
        title: 220,
        authority: 200,
        handler: 150,
      }

      const renderAuthorityIssuedName = record =>
        record.authority_issued_name || record.other_authority_issued || ''

      return [
        {
          title: 'Số đến',
          width: WIDTH.incoming,
          render: record => (
            <UnreadText text={record.incoming_number} width={WIDTH.incoming} />
          ),
        },
        {
          title: 'Số hiệu',
          width: WIDTH.number,
          render: record => (
            <UnreadText text={record.document_number} width={WIDTH.number} />
          ),
        },
        {
          title: 'Ngày đến',
          width: WIDTH.incomingDate,
          render: record => (
            <UnreadText
              text={moment(record.incoming_date).format(DATE_FORMAT_DEFAULT)}
              width={WIDTH.incomingDate}
            />
          ),
        },
        {
          title: 'Ngày văn bản',
          width: WIDTH.issuedDate,
          render: record =>
            record.date_issued ? (
              <UnreadText
                text={moment(record.date_issued).format(DATE_FORMAT_DEFAULT)}
                width={WIDTH.issuedDate}
              />
            ) : (
              <TextWrap>
                <EmptyText>-------------------------</EmptyText>
              </TextWrap>
            ),
        },
        {
          title: 'Trạng thái',
          align: 'center',
          width: WIDTH.status,
          render: record => (
            <div style={{ display: 'flex', justifyContent: 'center' }}>
              {StatusTag(
                TYPE_STATUS.INTERNAL_DOCUMENT_INCOMING,
                record.document_status
              )}
            </div>
          ),
        },
        {
          title: 'Trích yếu',
          width: WIDTH.title,
          render: record => (
            <UnreadText text={record.title} width={WIDTH.title} />
          ),
        },
        {
          title: 'Cơ quan ban hành',
          width: WIDTH.authority,
          render: record => (
            <UnreadText
              text={renderAuthorityIssuedName(record)}
              width={WIDTH.authority}
            />
          ),
        },
        {
          title: 'Lãnh đạo xử lý',
          width: WIDTH.handler,
          render: record =>
            record.handler ? (
              <UnreadText text={record.handler} width={WIDTH.handler} />
            ) : (
              <TextWrap>
                <EmptyText>-------------------------</EmptyText>
              </TextWrap>
            ),
        },
        {
          title: <div style={{ textAlign: 'center' }}>Tác vụ</div>,
          key: 'action',
          width: 80,
          fixed: 'right',
          render: record => (
            <Tooltip title="Khôi phục">
              <div style={{ display: 'flex', justifyContent: 'center' }}>
                <div
                  onClick={() => handleRestoreDocument(record)}
                  style={{
                    backgroundColor: '#fff',
                    border: '1px solid #d9d9d9',
                    borderRadius: 6,
                    width: 32,
                    height: 32,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    cursor: 'pointer',
                    boxShadow: '0 1px 2px rgba(0, 0, 0, 0.05)',
                  }}>
                  <UndoOutlined style={{ color: '#1890ff', fontSize: 16 }} />
                </div>
              </div>
            </Tooltip>
          ),
        },
      ]
    }, [])

    return (
      <Card>
        <TableActionWrapper>
          <TableComponent
            rowKey={record => record.id}
            dataSource={internalDocumentIncomingDeletedList}
            columns={tableColumns}
            pagination={false}
            scroll={{ x: 1000 }}
            loading={{
              tip: 'Đang tải dữ liệu...',
              spinning: loadingAnimationStore.tableLoading,
            }}
            locale={{ emptyText: <Empty description="Không có dữ liệu" /> }}
          />

          <TableFooterActionWrapper>
            <Pagination
              current={currentPage + 1}
              pageSize={pageSize}
              total={internalDocumentIncomingListTotalCount}
              onChange={page =>
                setQueryParamsDeleted({
                  ...queryParamsDeleted,
                  page: page - 1,
                  size: pageSize,
                })
              }
              showSizeChanger={false}
              hideOnSinglePage
            />
          </TableFooterActionWrapper>
        </TableActionWrapper>
      </Card>
    )
  })
)

export default IncomingPanel
